import { Add, FilterAltOff, Search } from '@mui/icons-material';
import { Badge, Box, Button, IconButton, TextField, Tooltip } from '@mui/material';
import useManagementRights from 'hooks/rights/useManagementRights';
import React from 'react';
import useApi from 'hooks/useApi';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { clearSelectedFilterAction, updateParamsFilterAction } from 'store/actions/filterActions';
import { useSelector } from 'store/hooks';
import { useAppState } from 'store/Provider';
import { OrdersTabState } from 'types/enums/OrdersTabState';
import useTextSearch from '../../../../hooks/useTextSearch';
import { openDrawerAction } from '../../../../store/actions/drawerActions';
import { selectFilterSelectedSize } from '../../../../store/selectors/filterSelectors';
import OrderApiService from 'api/OrderApiService';
import useManagementHelper from 'features/Management/services/useManagementHelper';
import './ManagementHeader.scss';

const ManagementHeader = () => {
  const [t] = useTranslation();
  const { makeCall } = useApi();
  const { dispatch } = useAppState();
  const { app, management, filter } = useSelector((state) => state);
  const currentTab = useSelector((state) => state.management.pageHandler.currentTab);
  const history = useHistory();  const filterSelectedSize = useSelector(selectFilterSelectedSize);
  const managementRight = useManagementRights();
  const { textSearch, setTextSearch, debouncedOnChange } = useTextSearch();
  const { isPathForProduction, isPathForQuotes } = useManagementHelper();

  const fetchFilterParams = React.useCallback(async () => {
    if (currentTab !== OrdersTabState.NULL && currentTab !== OrdersTabState.ARCHIVED) {
      const response = await makeCall(OrderApiService.fetchParamsFilterOrder(currentTab));
      if (response && response['super-filters']) {
        const items = response['super-filters'];
        // Transform order_ids array ? array of {id, name}
        const ids = (response['super-filters']?.order_ids || []).map((id: any) => ({
          id,
          name: id,
        }));
        // Rebuild the filters object with transformed order_ids
        const updatedFilters = {
          ...items,
          id: ids,
        };
        dispatch(updateParamsFilterAction(updatedFilters));
      }
    } else if (currentTab === OrdersTabState.ARCHIVED) {
      let status;
      if (isPathForProduction()) {
        status = 'production';
      } else if (isPathForQuotes()) {
        status = 'drafted';
      }
      if (status) {
        const response = await makeCall(OrderApiService.fetchParamsFilterOrder(status, { is_archived: true }));
        if (response && response['super-filters']) {
          dispatch(updateParamsFilterAction(response['super-filters']));
        }
      }
    }
  }, []);


  const resetFilters = () => {
    dispatch(clearSelectedFilterAction());
    delete filter.selected.mission_advancement_id;
    fetchFilterParams();
  };

  return (
    <Box sx={{ display: 'flex', mb: 4, alignItems: { md: 'center' }, flexDirection: { xs: 'column', md: 'row' } }}>
      {management.pageHandler.currentTab === OrdersTabState.QUOTE && managementRight.canCreateOrder && (
        <Button
          variant="contained"
          LinkComponent={Link}
          startIcon={<Add />}
          onClick={() => history.push(`/${app.customer?.slug}/catalogs?create_quote=true`)}
          sx={{ mb: { xs: 3, md: 0 } }}
        >
          {t('Create_New_Quote')}
        </Button>
      )}
      {management.pageHandler.currentTab === OrdersTabState.MATERIAL_DRAFT && managementRight.canCreateOrder && (
        <Button
          variant="contained"
          LinkComponent={Link}
          startIcon={<Add />}
          onClick={() => history.push(`/${app.customer?.slug}/orders/production/create_order`)}
          sx={{ mb: { xs: 3, md: 0 } }}
        >
          {t('Create_New_Order')}
        </Button>
      )}
      {filter.items.length > 0 && (
        <Box sx={{ ml: { md: 'auto' }, display: 'flex', alignItems: 'center' }}>
          {filterSelectedSize > 0 && (
            <Box>
              <Tooltip title={t('Clear_Filters') as string}>
                <IconButton
                  onClick={() => {
                    resetFilters();
                  }}
                  color="primary"
                  sx={{ mr: 2 }}
                >
                  <FilterAltOff />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          <Tooltip title={t('Search_By_Name_Or_Ref') as string}>
            <TextField
              placeholder={t('Search')}
              variant="standard"
              InputProps={{
                endAdornment: <Search sx={{ color: 'neutral.main' }} />,
              }}
              value={textSearch}
              onChange={(e) => {
                debouncedOnChange();
                setTextSearch(e.target.value);
              }}
              sx={{ mr: 2, minWidth: { md: 260 } }}
            />
          </Tooltip>
          <Badge badgeContent={filterSelectedSize} color="primary">
            <Button
              onClick={() => {
                dispatch(openDrawerAction({ name: 'drawerFilters' }));
              }}
              variant="contained"
            >
              {t('Filters')}
            </Button>
          </Badge>
        </Box>
      )}
    </Box>
  );
};

export default ManagementHeader;
